<!-- AdjustmentModals.vue -->
<template>
  <div>
    <!-- Balance Adjustment Modal -->
    <div class="modal fade adjustment-modal" tabindex="-1" role="dialog" aria-labelledby="adjustmentModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-uppercase" id="adjustmentModalLabel">Balance Adjustment</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <p><i class="mdi mdi-information-outline me-2"></i> You are making a balance adjustment for property <strong>{{ property.LRNumber }}</strong>.</p>
                  <p>Current outstanding balance: <strong>KES {{ numberFormat(property.currentBalance || property.outstandingArrears || 0) }}</strong></p>
                </div>
              </div>

              <div class="col-12">
                <div class="form-group mb-3">
                  <label for="adjustmentType" class="form-label">Adjustment Type</label>
                  <select v-model="adjustmentType" id="adjustmentType" class="form-select">
                    <option value="increase">Increase Balance (Add to Amount Owed)</option>
                    <option value="decrease">Decrease Balance (Reduce Amount Owed)</option>
                  </select>
                </div>
              </div>

              <div class="col-12">
                <div class="form-group mb-3">
                  <label for="adjustmentAmount" class="form-label">Adjustment Amount (KES)</label>
                  <input v-model="adjustmentAmount" type="number" class="form-control" id="adjustmentAmount" min="0" step="0.01">
                </div>
              </div>

              <div class="col-12">
                <div class="form-group mb-3">
                  <label for="adjustmentReason" class="form-label">Reason for Adjustment</label>
                  <textarea v-model="adjustmentReason" class="form-control" id="adjustmentReason" rows="3" placeholder="Please provide a valid reason for this adjustment"></textarea>
                </div>
              </div>

              <div class="col-12 mt-3">
                <div class="alert" :class="{'alert-danger': adjustmentType === 'increase', 'alert-success': adjustmentType === 'decrease'}">
                  <p class="mb-0"><strong>Summary:</strong></p>
                  <p class="mb-0">Current Balance: KES {{ numberFormat(property.currentBalance || property.outstandingArrears || 0) }}</p>
                  <p class="mb-0">Adjustment: {{ adjustmentType === 'increase' ? '+' : '-' }} KES {{ numberFormat(adjustmentAmount) }}</p>
                  <p class="mb-0"><strong>New Balance: KES {{ numberFormat(calculateNewBalance()) }}</strong></p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button @click="handleAdjustment" type="button" class="btn" :class="{'btn-danger': adjustmentType === 'increase', 'btn-success': adjustmentType === 'decrease'}" data-bs-dismiss="modal">
              <i class="mdi mdi-cash-refund me-1"></i> Apply Adjustment
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Adjustment History Modal -->
    <div class="modal fade adjustment-history-modal" tabindex="-1" role="dialog" aria-labelledby="adjustmentHistoryModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-uppercase" id="adjustmentHistoryModalLabel">Adjustment History</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <p><i class="mdi mdi-information-outline me-2"></i> Adjustment history for property <strong>{{ property.LRNumber }}</strong>.</p>
                </div>
              </div>

              <div class="col-12">
                <div v-if="adjustmentHistory.length === 0" class="text-center py-3">
                  <p>No adjustment history found for this property.</p>
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-bordered table-striped">
                    <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Before</th>
                      <th>After</th>
                      <th>Reason</th>
                      <th>Adjusted By</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(adjustment, index) in adjustmentHistory" :key="index">
                      <td>{{ formatDate(adjustment.adjustment_date) }}</td>
                      <td>
                        <span v-if="adjustment.adjustment_type === 'increase'" class="badge bg-danger">Increase</span>
                        <span v-else class="badge bg-success">Decrease</span>
                      </td>
                      <td>KES {{ numberFormat(adjustment.adjustment_amount) }}</td>
                      <td>KES {{ numberFormat(adjustment.amount_before) }}</td>
                      <td>KES {{ numberFormat(adjustment.amount_after) }}</td>
                      <td>{{ adjustment.reason }}</td>
                      <td>{{ adjustment.adjusted_by }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdjustmentModals',
  props: {
    property: {
      type: Object,
      default: () => ({})
    },
    adjustmentHistory: {
      type: Array,
      default: () => []
    }
  },
  emits: ['make-adjustment'],
  data() {
    return {
      adjustmentType: 'decrease',
      adjustmentAmount: 0,
      adjustmentReason: ''
    }
  },
  methods: {
    calculateNewBalance() {
      if (!this.property) return 0;

      // Prefer currentBalance (the primary column) and fall back to the
      // legacy outstandingArrears mirror.
      const currentBalance = parseFloat(
        this.property.currentBalance || this.property.outstandingArrears || 0
      );
      const adjustmentAmount = parseFloat(this.adjustmentAmount) || 0;

      if (this.adjustmentType === 'increase') {
        return currentBalance + adjustmentAmount;
      } else {
        return Math.max(0, currentBalance - adjustmentAmount);
      }
    },
    handleAdjustment() {
      if (!this.adjustmentAmount || this.adjustmentAmount <= 0) {
        alert("Please enter a valid adjustment amount greater than zero.");
        return;
      }

      if (!this.adjustmentReason.trim()) {
        alert("Please provide a reason for this adjustment.");
        return;
      }

      this.$emit('make-adjustment', {
        property: this.property,
        adjustmentType: this.adjustmentType,
        adjustmentAmount: this.adjustmentAmount,
        adjustmentReason: this.adjustmentReason
      });

      // Reset form
      this.adjustmentType = 'decrease';
      this.adjustmentAmount = 0;
      this.adjustmentReason = '';
    },
    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num)
    },
    formatDate(date) {
      let d = new Date(date);
      let day = d.getDate();
      let month = d.toLocaleString('en-US', { month: 'short' });
      let year = d.getFullYear();

      let suffix = '';
      if (day % 10 === 1 && day !== 11) {
        suffix = 'st';
      } else if (day % 10 === 2 && day !== 12) {
        suffix = 'nd';
      } else if (day % 10 === 3 && day !== 13) {
        suffix = 'rd';
      } else {
        suffix = 'th';
      }
      return `${day}${suffix} ${month} ${year}`;
    }
  }
}
</script>